import { useForm } from "@tanstack/react-form";
import { Button } from "../../../components/Button";
import { Form } from "../../../components/Form";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { FormActions } from "../../../components/FormActions";
import { Input } from "../../../components/Input";
import styled from "styled-components";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { useTranslation } from "react-i18next";
import type {
  RestIntegration,
  RestUpdateIntegration,
} from "@KassaroAB/shared/types/integrations/integration";
import type { ChainId } from "@KassaroAB/shared/types/ids";
import { useUpdateIntegrationMutation } from "../../../query/integrationsQuery";

const ErrorMessage = styled.div`
  color: ${colors.danger};
  font-size: ${fontSizes.small}px;
  margin-top: ${spacing.x05}px;
`;

interface NameOnlyFormProps {
  chainId?: ChainId;
  integration?: RestIntegration;
  onSuccess: () => void;
  onError: () => void;
  onCancel: () => void;
}

export const NameOnlyForm = ({
  chainId,
  integration,
  onSuccess,
  onError,
  onCancel,
}: NameOnlyFormProps) => {
  const { t } = useTranslation();
  const { mutate: updateMutation } = useUpdateIntegrationMutation(chainId ?? null);

  const form = useForm({
    defaultValues: {
      name: integration?.name || "",
    },
    onSubmit: ({ value }) => {
      if (!integration) {
        return;
      }
      const payload: Partial<RestUpdateIntegration> = {
        type: integration.type,
        name: value.name,
      };
      updateMutation(
        { integrationId: integration.id, integration: payload },
        { onSuccess: () => onSuccess(), onError: () => onError() },
      );
    },
  });

  if (!integration) {
    return null;
  }

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <form.Field
        name="name"
        validators={{
          onChange: ({ value }) => (!value ? t("integrationDetails.nameRequired") : undefined),
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("integrationDetails.nameLabel")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("integrationDetails.namePlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <FormActions>
        <Button type="button" $color="secondary" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button type="submit">{t("common.update")}</Button>
      </FormActions>
    </Form>
  );
};
